import React, {FC, useEffect} from 'react';
import HLModal, {HLModalSizeType} from '../../shared/components/modal/HLModal';
import HLButton, {HLButtonStyleType} from '../../shared/components/button/HLButton';
import {createLessonsFetch} from "../../app/axios/lessonFetches";
import {useForm} from "react-hook-form";
import {LessonCreateRequest, LessonType, LessonTypeNameMap} from "../../shared/types/lesson";
import HLErrorMessage from "../../shared/components/error-message/HLErrorMessage";
import HLFormInput from "../../shared/components/input/HLFormInput";
import {processFormServerError} from "../../shared/utils/processFormServerError";
import {AgreementStructure} from "../../shared/types/agreement";
import classes from "./UpdateLessonModal.module.scss";
import HLLine from "../../shared/components/line/HLLine";
import HLFormSelect from "../../shared/components/select/HLFormSelect";

interface CreateLessonsModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    agreement: AgreementStructure;
    onSuccess: () => void;
}

const lessonTypeOptions = Object.entries(LessonTypeNameMap).map(([value, label]) => ({
    value,
    label
}));

interface CreateLessonFormData {
    lessonDate: string;
    type: LessonType;
    students: CreateLessonStudentFormData[];
    teacher: CreateLessonTeacherFormData;
}

interface CreateLessonStudentFormData {
    studentId: number;
    lessonPrice: number;

    firstName: string;
    lastName: string;
}

interface CreateLessonTeacherFormData {
    teacherRate: number

    firstName: string;
    lastName: string;
}

const AdminCreateLessonsModal: FC<CreateLessonsModalProps> = (
    {
        open,
        setOpen,
        agreement,
        onSuccess,
    }) => {
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        setError,
        watch,
        formState: {errors, isSubmitting}
    } = useForm<CreateLessonFormData>({
        mode: 'onChange',
    });

    register('students');

    useEffect(() => {
        if (agreement && open) {
            setValue('type', LessonType.USUAL)
            setValue('teacher', {
                teacherRate: agreement.teacher.teacherRate,
                firstName: agreement.teacher.firstName,
                lastName: agreement.teacher.lastName
            })
            setValue('students', agreement.students.map(student => ({
                studentId: student.id,
                lessonPrice: student.lessonPrice,
                firstName: student.firstName,
                lastName: student.lastName
            })))
        }
    }, [agreement, open, setValue]);

    const students: CreateLessonStudentFormData[] = watch('students');
    const teacher: CreateLessonTeacherFormData = watch('teacher');

    const onSubmit = async (data: CreateLessonFormData) => {
        try {
            const request: LessonCreateRequest = {
                agreementId: agreement.id,
                lessonDate: data.lessonDate,
                type: data.type,
                teacherRate: data.teacher.teacherRate,
                students: data.students.map(student => ({
                    studentId: student.studentId,
                    lessonPrice: student.lessonPrice,
                }))
            };

            await createLessonsFetch(request);
            closeModal()
            onSuccess()
        } catch (err: any) {
            processFormServerError(err, setError);
        }
    }

    const closeModal = () => {
        reset()
        setOpen(false)
    }

    return (
        <HLModal open={open} onClose={closeModal} title="Создать урок" sizeType={HLModalSizeType.MEDIUM}>
            <form onSubmit={handleSubmit(onSubmit)} className="columnContainer">
                <HLErrorMessage error={errors.root?.message}/>

                <h2 className={classes.header}>Общая информация</h2>
                <HLFormInput
                    id="lessonDate"
                    type="datetime-local"
                    {...register('lessonDate', {
                        required: 'Дата урока обязательна'
                    })}
                    error={errors.lessonDate?.message}
                    placeholder="Дата и время урока"
                >Дата и время урока</HLFormInput>
                <HLFormSelect
                    id="role"
                    {...register('type', {required: 'Тип обязателен'})}
                    value={watch('type') || ''}
                    options={lessonTypeOptions}
                    error={errors.type?.message}
                />

                <h2 className={classes.header}>Учитель</h2>
                {teacher && <>
                    <h4>{teacher.firstName} {teacher.lastName}</h4>
                    <HLFormInput
                        id="teacher.teacherRate"
                        type="number"
                        step="0.01"
                        {...register('teacher.teacherRate')}
                        error={errors.teacher?.teacherRate?.message}
                    >Ставка учителя</HLFormInput>
                </>}

                <h2 className={classes.header}>Ученики</h2>
                {students && students.map((student: CreateLessonStudentFormData, index: number) => (
                    <div key={student.studentId} className={`columnContainer`}>
                        <h4>{student.firstName} {student.lastName || ''}</h4>
                        <HLFormInput
                            id={`students.${index}.lessonPrice`}
                            type="number"
                            step="0.01"
                            {...register(`students.${index}.lessonPrice`, {
                                required: 'Цена обязательна'
                            })}
                            error={errors.students?.[index]?.lessonPrice?.message}
                        >Цена урока</HLFormInput>
                        {students.length !== index + 1 && <HLLine/>}
                    </div>
                ))}

                <div className="inlineContainer">
                    <HLButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Создать
                    </HLButton>
                    <HLButton
                        type="button"
                        onClick={closeModal}
                        styleType={HLButtonStyleType.GRAY}
                    >
                        Отмена
                    </HLButton>
                </div>
            </form>
        </HLModal>
    );
};

export default AdminCreateLessonsModal;